/*!
 * Xtenia Score - Comparison Code System
 * Copyright (c) 2026 Xtenia. All rights reserved.
 * https://www.xtenia.com/
 *
 * Generates compact, shareable codes from a Xtenia Score result so users
 * can compare scores with friends without any backend storage.
 *
 * Format: WS-XXXX-XXXX-XXXX
 *   - "WS-" prefix for recognition
 *   - 12 chars in 3 groups of 4, base32 alphabet (no I/O/0/1)
 *   - Encodes: total, tier index, archetype index, weakest pillar, strongest pillar
 *   - Last char is a checksum digit to catch typos
 *
 * The code IS the data. Nothing transmitted, nothing stored on a server.
 * Codes are NOT cryptographically secure - users could craft a fake "elite"
 * code if they wanted, but anyone can also lie when taking the test, so this
 * isn't worth defending against. The checksum prevents typos, not fraud.
 */

(function() {
  'use strict';

  window.WD = window.WD || {};
  window.WD.score = window.WD.score || {};
  var WS = window.WD.score;

  // Crockford base32 alphabet (no I, L, O, U for clarity)
  var ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  var ALPHA_MAP = {};
  for (var i = 0; i < ALPHABET.length; i++) {
    ALPHA_MAP[ALPHABET[i]] = i;
  }
  // Common typo corrections
  ALPHA_MAP['I'] = ALPHA_MAP['1'];
  ALPHA_MAP['L'] = ALPHA_MAP['1'];
  ALPHA_MAP['O'] = ALPHA_MAP['0'];
  ALPHA_MAP['U'] = ALPHA_MAP['V'];

  var TIERS = ['foundation', 'builder', 'athlete', 'specialist', 'elite'];
  var ARCHETYPES = ['athlete', 'streetlifter', 'specialist', 'foundation', 'powerhouse', 'mobility', 'hybrid'];
  var PILLARS = ['strength', 'skill', 'mobility', 'endurance', 'movement'];

  /**
   * Pack an integer into N base32 characters, zero-padded on the left.
   */
  function packInt(value, chars) {
    value = Math.max(0, Math.floor(value));
    var max = Math.pow(32, chars);
    if (value >= max) value = max - 1;
    var out = '';
    for (var i = 0; i < chars; i++) {
      out = ALPHABET[value % 32] + out;
      value = Math.floor(value / 32);
    }
    return out;
  }

  function unpackInt(s) {
    var value = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i).toUpperCase();
      var d = ALPHA_MAP[c];
      if (d == null) return null;
      value = value * 32 + d;
    }
    return value;
  }

  /**
   * Compute a single-char checksum over the rest of the code.
   * Simple modular sum — good enough to catch typos.
   */
  function checksum(s) {
    var sum = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charAt(i).toUpperCase();
      var d = ALPHA_MAP[c];
      if (d != null) sum = (sum * 31 + d) % 32;
    }
    return ALPHABET[sum];
  }

  /**
   * Encode a Xtenia Score result into a compact shareable code.
   *
   * @param {Object} result - The scoring engine output
   * @returns {string} Code formatted as "WS-XXXX-XXXX-XXXX"
   */
  function generateScoreCode(result) {
    if (!result || typeof result.total !== 'number') return null;

    // Pack each field into base32 chars
    // Total: 0-1000, fits in 2 chars (32^2 = 1024)
    var totalEnc = packInt(result.total, 2);

    // Tier index: 0-4, fits in 1 char
    var tierIdx = TIERS.indexOf(result.tier);
    if (tierIdx < 0) tierIdx = 0;
    var tierEnc = packInt(tierIdx, 1);

    // Archetype index: 0-6, fits in 1 char
    var archIdx = ARCHETYPES.indexOf(result.archetype);
    if (archIdx < 0) archIdx = 0;
    var archEnc = packInt(archIdx, 1);

    // 5 pillar scores, each 0-100, packed in 7 chars total
    // (100 needs 2 chars in base32, so 5 * 2 = 10 chars... too long)
    // Compress to 0-31 range (each pillar value / ~3.2)
    var pillars = result.pillars || {};
    var pEnc = '';
    for (var i = 0; i < PILLARS.length; i++) {
      var v = pillars[PILLARS[i]] || 0;
      var compressed = Math.round(v * 31 / 100);
      pEnc += packInt(compressed, 1);
    }
    // Total so far: 2 + 1 + 1 + 5 = 9 chars

    // Pad with version + reserved (1 char), bring total to 10 chars
    var version = packInt(1, 1); // version 1 of code format

    var payload = totalEnc + tierEnc + archEnc + pEnc + version;
    // payload is 10 chars

    // Add checksum (1 char) + 1 random fill char to round to 12
    var fillChar = ALPHABET[Math.floor(Math.random() * 32)];
    var withFill = payload + fillChar;
    var check = checksum(withFill);
    var code = withFill + check;
    // 12 chars total

    // Format as WS-XXXX-XXXX-XXXX
    return 'WS-' + code.substring(0, 4) + '-' + code.substring(4, 8) + '-' + code.substring(8, 12);
  }

  /**
   * Decode a Xtenia Score code back into a result-like object.
   *
   * @param {string} code - Code to decode
   * @returns {Object|null} Decoded result, or null if invalid
   */
  function parseScoreCode(code) {
    if (!code || typeof code !== 'string') return null;

    // Normalize: uppercase, strip spaces and dashes
    var clean = code.toUpperCase().replace(/[\s-]/g, '');

    // Strip the "WS" prefix
    if (clean.indexOf('WS') !== 0) return null;
    clean = clean.substring(2);

    // Must be exactly 12 chars
    if (clean.length !== 12) return null;

    // Verify checksum
    var payload = clean.substring(0, 11);
    var providedCheck = clean.charAt(11);
    var expectedCheck = checksum(payload);
    if (providedCheck !== expectedCheck) return null;

    // Decode fields
    var totalEnc = clean.substring(0, 2);
    var tierEnc = clean.substring(2, 3);
    var archEnc = clean.substring(3, 4);
    var pEnc = clean.substring(4, 9);
    var versionEnc = clean.substring(9, 10);

    var total = unpackInt(totalEnc);
    var tierIdx = unpackInt(tierEnc);
    var archIdx = unpackInt(archEnc);
    var version = unpackInt(versionEnc);

    if (total == null || tierIdx == null || archIdx == null) return null;

    var pillars = {};
    for (var i = 0; i < PILLARS.length; i++) {
      var compressed = unpackInt(pEnc.charAt(i));
      if (compressed == null) return null;
      pillars[PILLARS[i]] = Math.round(compressed * 100 / 31);
    }

    return {
      total: total,
      tier: TIERS[tierIdx] || 'foundation',
      archetype: ARCHETYPES[archIdx] || 'athlete',
      pillars: pillars,
      version: version
    };
  }

  /**
   * Compare two decoded results, returning a side-by-side comparison object.
   */
  function compareResults(mine, theirs) {
    if (!mine || !theirs) return null;

    var pillarComparison = {};
    for (var i = 0; i < PILLARS.length; i++) {
      var p = PILLARS[i];
      var mineP = mine.pillars[p] || 0;
      var theirsP = theirs.pillars[p] || 0;
      var winner = mineP === theirsP ? 'tied' : (mineP > theirsP ? 'mine' : 'theirs');
      pillarComparison[p] = {
        mine: mineP,
        theirs: theirsP,
        winner: winner,
        diff: Math.abs(mineP - theirsP)
      };
    }

    var totalWinner = mine.total === theirs.total
                      ? 'tied'
                      : (mine.total > theirs.total ? 'mine' : 'theirs');

    return {
      mine: mine,
      theirs: theirs,
      totalWinner: totalWinner,
      totalDiff: Math.abs(mine.total - theirs.total),
      pillars: pillarComparison
    };
  }

  // Expose
  WS.generateScoreCode = generateScoreCode;
  WS.parseScoreCode = parseScoreCode;
  WS.compareResults = compareResults;

})();